import { BringzzButton, BringzzIcon, BringzzBottomDrawer, BringzzInput, BringzzText, BringzzQrCode } from '@bringzz/components';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useParams } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import { useOrder } from 'context/OrderContext';
import useUser from 'hooks/useUser';
import { USER_ROLES } from 'utils/constants';
import { toast } from 'react-toastify';
import jsQR from 'jsqr';

const CAMERA_MODES = {
    CUSTOMER_PICKUP_HANDOVER: 'customer/pickup/handover',
    CUSTOMER_PICKUP_IDENTIFY: 'customer/pickup/identify',
    CUSTOMER_PICKUP_CONFIRM: 'customer/pickup/confirm',
    CUSTOMER_PICKUP_COLLECT: 'customer/pickup/collect',
    CUSTOMER_COLLECT_IDENTIFY: 'customer/collect/identify',
    CUSTOMER_COLLECT_CONFIRM: 'customer/collect/confirm',
    CUSTOMER_MAIL_CONFIRM: 'customer/mail/confirm',
    PHARMACY_PICKUP_HANDOVER: 'pharmacy/pickup/handover',
    PHARMACY_PICKUP_IDENTIFY: 'pharmacy/pickup/identify',
    PHARMACY_PICKUP_CONFIRM: 'pharmacy/pickup/confirm',
    PHARMACY_PICKUP_COLLECT: 'pharmacy/pickup/collect',
    PHARMACY_PICKUP_BAG: 'pharmacy/pickup/bag',
    PHARMACY_COLLECT_BAG: 'pharmacy/collect/bag',
    PHARMACY_COLLECT_IDENTIFY: 'pharmacy/collect/identify',
    PHARMACY_COLLECT_CONFIRM: 'pharmacy/collect/confirm',
    PHARMACY_MAIL_BAG: 'pharmacy/mail/bag',
    SHOPPER_PICKUP_HANDOVER: 'shopper/pickup/handover',
    SHOPPER_PICKUP_IDENTIFY: 'shopper/pickup/identify',
    SHOPPER_PICKUP_CONFIRM: 'shopper/pickup/confirm',
    SHOPPER_PICKUP_COLLECT: 'shopper/pickup/collect',

};

// UUID validation regex pattern
const isValidUUID = (str) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str.trim());
};

// Custom hook for QR scanning using jsQR (works in all browsers)
const useQrScanner = (videoRef, onDetect, isEnabled) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const lastScanRef = useRef(null);

    useEffect(() => {
        if (!isEnabled) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            return;
        }

        // Create canvas for frame capture
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
        }
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const scanFrame = () => {
            const video = videoRef.current;
            if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'dontInvert'
                });

                if (code && code.data) {
                    // Debounce: don't fire for same code within 2 seconds
                    const now = Date.now();
                    if (lastScanRef.current !== code.data || now - (lastScanRef.current?.time || 0) > 2000) {
                        console.log('[QrScanner] QR code detected:', code.data);
                        lastScanRef.current = { data: code.data, time: now };
                        onDetect({ text: code.data });
                    }
                }
            }

            animationRef.current = requestAnimationFrame(scanFrame);
        };

        // Start scanning after a short delay to let the video initialize
        const timeoutId = setTimeout(() => {
            console.log('[QrScanner] Starting QR scan loop');
            scanFrame();
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [videoRef, onDetect, isEnabled]);
};

// Hook to start camera stream
const useCamera = (videoRef) => {
    useEffect(() => {
        let stream = null;

        const startCamera = async () => {
            try {
                console.log('[Camera] Requesting camera access...');
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    console.log('[Camera] Camera stream started');
                }
            } catch (err) {
                console.error('[Camera] Error accessing the camera:', err);
            }
        };

        startCamera();

        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
                console.log('[Camera] Camera stream stopped');
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [videoRef]);
};

const useCameraActions = ({ idCode, orderDetails, user, type, activeStep, proceedToNextStep, navigate, setOrderPackedDrawerOpened, cameraMode, setScannedBagCodes }) => {
    const { sendRequest, loading } = useRequest();

    const handleHandover = async () => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/authenticateParties',
                data: {
                    idCode,
                    orderUUID: orderDetails.uuid || orderDetails.integrationId
                }
            });

            if (response.status === 'success') {
                proceedToNextStep();
                if (user.userRole === USER_ROLES.PHARMACY_ROLE) {
                    if (cameraMode === CAMERA_MODES.PHARMACY_COLLECT_IDENTIFY) {
                        const nextStep = '/camera/pharmacy/collect/confirm';
                        const instructions = "PHARMACY_SCAN_BAG_INSTRUCTIONS";
                        navigate('/instruction', {
                            state: {
                                instructions,
                                nextStep
                            }
                        });
                        return;
                    }
                    navigate('/matchmaking/pharmacy/6');
                } else if (user.userRole === USER_ROLES.CONSUMER_ROLE) {
                    navigate(`/matchmaking/customer/${activeStep <= 5 ? '5' : '11'}`);
                } else {
                    navigate(`/matchmaking/${type}/5`);
                }
            } else if (response.status === 'error') {
                toast.error(response.message || 'Authentication failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to authenticate. Please try again.');
        }
    };

    const handleConfirm = async (bagCodes = []) => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/verifyPackage',
                data: {
                    orderUUID: orderDetails.uuid || orderDetails.integrationId,
                    bagCodes: bagCodes
                }
            });

            if (response.status === 'success') {
                proceedToNextStep();
                // Clear the bag codes after successful submission
                setScannedBagCodes([]);
                if (user.userRole === USER_ROLES.PHARMACY_ROLE) {
                    if (
                        cameraMode === CAMERA_MODES.PHARMACY_COLLECT_BAG ||
                        cameraMode === CAMERA_MODES.PHARMACY_COLLECT_CONFIRM
                    ) {
                        navigate('/matchmaking/pharmacy/4');
                        return;
                    } else if (
                        cameraMode === CAMERA_MODES.PHARMACY_PICKUP_COLLECT
                    ) {
                        navigate('/matchmaking/pharmacy/7');
                        return;
                    }
                    navigate('/matchmaking/package/7');
                } else if (user.userRole === USER_ROLES.SHOPPER_ROLE) {
                    console.log('=============XXXXXXX============')
                    if (activeStep == 8) { // Final handover no prescription
                        console.log('=============XXXXXXX============111')
                        navigate('/matchmaking/shopper/9');
                    } else {
                        console.log('========0=00=0=0=0=0============')
                        navigate('/matchmaking/shopper/6');
                    }
                } else if (user.userRole === USER_ROLES.CONSUMER_ROLE) {
                    if (cameraMode === CAMERA_MODES.CUSTOMER_COLLECT_CONFIRM) {
                        navigate('/matchmaking/customer/6');
                        return;
                    }
                    if (cameraMode === CAMERA_MODES.CUSTOMER_MAIL_CONFIRM) {
                        navigate('/matchmaking/customer/5');
                        return;
                    }
                    navigate('/matchmaking/customer/13');
                } else {
                    console.error("camera page: user role not found");
                }
            } else if (response.status === 'error') {
                toast.error(response.message || 'Package verification failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to verify package. Please try again.');
        }
    };

    const onContinue = async (bagCodes = []) => {
        setOrderPackedDrawerOpened(false);
        await sendRequest({
            method: 'POST',
            url: `/module/order/orderIsReady`,
            data: {
                "orderUUID": orderDetails.integrationId,
                "bagCodes": bagCodes
            }
        }).then((response) => {
            if (response.status === 'success') {
                proceedToNextStep()
                // Clear the bag codes after successful submission
                setScannedBagCodes([]);
                navigate('/matchmaking/pharmacy/1');
            } else {
                console.log("orderIsReady error");
                toast.error(response.message || 'Failed to mark order as ready');
            }
        }).catch((error) => {
            console.error(error);
            toast.error('Failed to update order status. Please try again.');
        })
    };

    return { handleHandover, handleConfirm, loading, onContinue };
};

const CameraOverlay = () => (
    <div className="absolute top-1/2 left-1/2 w-64 h-64 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none grid grid-cols-3 grid-rows-3">
        <div className="border-t-2 border-l-2 border-white bg-opacity-50 col-start-1 row-start-1" />
        <div className="border-t-2 border-r-2 border-white bg-opacity-50 col-start-3 row-start-1" />
        <div className="border-b-2 border-l-2 border-white bg-opacity-50 col-start-1 row-start-3" />
        <div className="border-b-2 border-r-2 border-white bg-opacity-50 col-start-3 row-start-3" />
    </div>
);

const CameraInstructions = ({ cameraMode, packages }) => {
    console.log(packages)
    const numPackages = Array.isArray(packages) ? packages.length : 0;
    const hasPackages = numPackages > 0;
    const instructions = {
        [CAMERA_MODES.CUSTOMER_PICKUP_HANDOVER]: 'Scan the QR-Code on the shopper phone to verify that he belongs to the order.',
        [CAMERA_MODES.CUSTOMER_PICKUP_IDENTIFY]: 'Scan the QR-Code on the shopper phone to verify that he belongs to the order.',
        [CAMERA_MODES.CUSTOMER_PICKUP_CONFIRM]: 'Scan the QR-Code on the order to confirm handover from the shopper',
        [CAMERA_MODES.CUSTOMER_PICKUP_COLLECT]: 'Scan the QR-Code on the order to confirm pickup from pharmacy',
        [CAMERA_MODES.PHARMACY_PICKUP_HANDOVER]: 'Scan the QR-Code on the shopper phone to verify that he belongs to the order.',
        [CAMERA_MODES.PHARMACY_PICKUP_IDENTIFY]: 'Scan the QR-Code on the shopper phone to verify that he belongs to the order.',
        [CAMERA_MODES.PHARMACY_PICKUP_CONFIRM]: 'Scan the QR-Code on your order bag to confirm that you have received the correct order.',
        [CAMERA_MODES.PHARMACY_PICKUP_COLLECT]: 'Scan the QR-Code on the order bag and seal it.',
        [CAMERA_MODES.CUSTOMER_COLLECT_IDENTIFY]: 'Scan the QR-Code on the order bag and seal it.',
        [CAMERA_MODES.PHARMACY_PICKUP_BAG]: 'Scan the QR-Code on the order bag and seal it.',
        [CAMERA_MODES.PHARMACY_COLLECT_IDENTIFY]: 'Scan the QR-Code on the customer phone to verify that he belongs to the order.',
        [CAMERA_MODES.PHARMACY_COLLECT_BAG]: 'Scan the QR-Code on the order bag and handover the order to the customer.',
        [CAMERA_MODES.CUSTOMER_COLLECT_CONFIRM]: `Scan the QR-Code on the order bag${hasPackages ? `s (${numPackages} bag${numPackages === 1 ? '' : 's'})` : ''} to confirm that you have received the correct order.`,
        [CAMERA_MODES.PHARMACY_MAIL_BAG]: 'Scan the QR-Code on the order bag and ship it to the customer.',
        [CAMERA_MODES.CUSTOMER_MAIL_CONFIRM]: `Scan the QR-Code on the order bag${hasPackages ? `s (${numPackages} bag${numPackages === 1 ? '' : 's'})` : ''} to confirm that you have received the correct order.`,
        [CAMERA_MODES.PHARMACY_COLLECT_CONFIRM]: 'Scan the QR-Code on the order bag and give it to the customer.',
        [CAMERA_MODES.SHOPPER_PICKUP_COLLECT]: 'Take over the order',
        [CAMERA_MODES.SHOPPER_PICKUP_HANDOVER]: 'Handover the order to the customer.'
    };

    return (
        <div className='absolute top-32 text-center w-full px-8'>
            {instructions[cameraMode] && (
                <BringzzText tag='h4' className='bg-black/40 p-2 text-white font-normal'>
                    {instructions[cameraMode]}
                </BringzzText>
            )}
        </div>
    );
};

const IdCodeDrawer = ({ isOpen, onClose, idCode, setIdCode, onSubmit, loading }) => (
    <BringzzBottomDrawer
        isOpen={isOpen}
        close={onClose}
        title='Enter ID-Code'
    >
        <div className='px-4 py-6'>
            <BringzzInput
                label='Id-Code'
                placeholder={'ID-Code'}
                value={idCode}
                onChange={(e, newIsValid, newValue) => setIdCode(newValue)}
            />
        </div>

        <div className='text-center mb-12 px-4'>
            <BringzzButton
                className='bg-magic-lilac w-full flex justify-center'
                onClick={() => {
                    onClose();
                    onSubmit();
                }}
                disabled={idCode.length === 0 || loading}
            >
                Apply
            </BringzzButton>
        </div>
    </BringzzBottomDrawer>
);

const OrderPackedDrawer = ({ isOpen, onClose, bagCount, onContinue, handleConfirm, onAddAnotherBag, loading, bagCodes, cameraMode, packages }) => {
    // Determine which handler to use based on camera mode
    const isOrderReady = cameraMode === CAMERA_MODES.PHARMACY_PICKUP_BAG ||
        cameraMode === CAMERA_MODES.PHARMACY_COLLECT_BAG ||
        cameraMode === CAMERA_MODES.PHARMACY_MAIL_BAG;

    // Determine if this is a customer-facing mode
    const isCustomerMode = cameraMode === CAMERA_MODES.CUSTOMER_COLLECT_CONFIRM ||
        cameraMode === CAMERA_MODES.CUSTOMER_MAIL_CONFIRM;

    const handleContinue = () => {
        onClose(); // Close the drawer
        if (isOrderReady) {
            onContinue(bagCodes);
        } else {
            handleConfirm(bagCodes);
        }
    };

    // Get appropriate drawer text based on mode
    const getDrawerText = () => {
        const numPackages = Array.isArray(packages) ? packages.length : 0;

        if (isCustomerMode) {
            if (numPackages === 1) {
                return "Bag scanned successfully!";
            }
            return "Bag scanned successfully! You can continue scanning the remaining bags one by one.";
        }

        // For pharmacy/shopper modes
        if (numPackages === 1) {
            return "Bag scanned successfully!";
        }
        return "Bag scanned successfully! You can continue scanning the remaining bags one by one.";
    };

    return (
        <BringzzBottomDrawer
            isOpen={isOpen}
            close={onClose}
        >
            <div className='text-center mb-12 px-4'>
                <div className='flex justify-center items-center'>
                    <BringzzIcon
                        size="16"
                        icon="IconCheck"
                        folder="CustomIcons"
                        className="cursor-pointer bg-magic-lilac rounded-full p-3"
                    />
                </div>
                <BringzzText tag='h2' className='p-2 font-normal'>{isOrderReady ? "Order Packed!" : "Great!"}</BringzzText>
                <BringzzText tag='h4' className='p-2 font-normal'>
                    {getDrawerText()}
                </BringzzText>
                {!isOrderReady &&
                    <BringzzText tag='h4' className='p-2 font-normal'>{bagCount + 1}/{packages.length}</BringzzText>
                }
                <div className='flex flex-col gap-2'>
                    <BringzzButton className='bg-magic-lilac w-full flex justify-center' onClick={handleContinue} disabled={loading || (!isOrderReady && bagCount + 1 != packages.length)}>Continue</BringzzButton>
                    {isOrderReady &&
                        <BringzzButton className='border border-midnight-blue w-full flex justify-center' onClick={onAddAnotherBag} disabled={loading}>Add another bag</BringzzButton>
                    }
                    {!isOrderReady && (bagCount + 1 != packages.length) &&
                        <BringzzButton className='border border-midnight-blue w-full flex justify-center' onClick={onAddAnotherBag} disabled={loading}>Scan next bag</BringzzButton>
                    }

                </div>
            </div>
        </BringzzBottomDrawer>
    );
};

const CameraPage = () => {
    const videoRef = useRef(null);
    const { backPress, navigate } = useNavigation();
    const [failDrawerOpened, setFailDrawerOpened] = useState(false);
    const [orderPackedDrawerOpened, setOrderPackedDrawerOpened] = useState(false);
    const [bagCount, setBagCount] = useState(0);
    const [idCode, setIdCode] = useState('');
    const [scannedBagCodes, setScannedBagCodes] = useState([]);
    const { role, type, mode } = useParams();
    const cameraMode = CAMERA_MODES[`${role.toUpperCase()}_${type.toUpperCase()}_${mode.toUpperCase()}`];
    const [isProcessingScan, setIsProcessingScan] = useState(false);

    const { orderDetails, proceedToNextStep, activeStep } = useOrder();

    console.log(orderDetails)

    const { getUser } = useUser();
    const user = getUser();

    // Determine if camera/scanner should be active
    const shouldShowCamera = mode !== "collect" && cameraMode !== CAMERA_MODES.CUSTOMER_PICKUP_CONFIRM;
    const scannerEnabled = shouldShowCamera && !isProcessingScan && !orderPackedDrawerOpened && !failDrawerOpened;

    // Start camera
    useCamera(videoRef);

    const { handleHandover, handleConfirm, loading, onContinue } = useCameraActions({
        idCode,
        orderDetails,
        user,
        type,
        activeStep,
        proceedToNextStep,
        navigate,
        setOrderPackedDrawerOpened,
        cameraMode,
        setScannedBagCodes
    });

    const submitCode = async () => {
        const trimmedIdCode = idCode.trim();

        if (cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_HANDOVER ||
            cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_IDENTIFY ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_IDENTIFY ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_IDENTIFY) {
            handleHandover();
        } else if (cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_COLLECT_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_MAIL_CONFIRM ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_CONFIRM ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.SHOPPER_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.SHOPPER_PICKUP_HANDOVER) {
            // Allow scanning multiple bags for verifyPackage
            if (trimmedIdCode) {
                // Validate UUID format
                if (!isValidUUID(trimmedIdCode)) {
                    toast.error('Invalid code format. Please scan a valid QR code or enter a valid UUID.');
                    return;
                }
                // Check if the code has already been scanned
                if (scannedBagCodes.includes(trimmedIdCode)) {
                    toast.error('This bag has already been scanned!');
                    return;
                }
                // Add the scanned/entered code to the array
                setScannedBagCodes([...scannedBagCodes, trimmedIdCode]);
            }
            setOrderPackedDrawerOpened(true);
        } else if (
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_BAG ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_BAG ||
            cameraMode === CAMERA_MODES.PHARMACY_MAIL_BAG
        ) {
            if (trimmedIdCode) {
                // Validate UUID format
                if (!isValidUUID(trimmedIdCode)) {
                    toast.error('Invalid code format. Please scan a valid QR code or enter a valid UUID.');
                    return;
                }
                // Check if the code has already been scanned
                if (scannedBagCodes.includes(trimmedIdCode)) {
                    toast.error('This bag has already been scanned!');
                    return;
                }
                // Add the scanned/entered code to the array
                setScannedBagCodes([...scannedBagCodes, trimmedIdCode]);
            }
            setOrderPackedDrawerOpened(true);
        } else {
            console.error("camera page: camera mode not found");
        }
    };

    const onAddAnotherBag = () => {
        setOrderPackedDrawerOpened(false);
        setIdCode('');
        setBagCount(bagCount + 1);
        setIsProcessingScan(false);
    };

    // Handle QR code detection from camera
    const handleQrDetected = useCallback((detected) => {
        console.log('[Camera] handleQrDetected called with:', detected);

        if (isProcessingScan || orderPackedDrawerOpened || failDrawerOpened) {
            console.log('[Camera] Skipping - already processing or drawer open', { isProcessingScan, orderPackedDrawerOpened, failDrawerOpened });
            return; // Don't process if already handling a scan or drawer is open
        }

        const scannedValue = detected?.text?.trim();
        if (!scannedValue) {
            console.log('[Camera] No text in detected value');
            return;
        }

        console.log('[Camera] QR code detected:', scannedValue);

        // Validate UUID format
        if (!isValidUUID(scannedValue)) {
            console.log('[Camera] Invalid UUID format, ignoring:', scannedValue);
            return;
        }

        // Check if already scanned
        if (scannedBagCodes.includes(scannedValue)) {
            toast.error('This bag has already been scanned!');
            return;
        }

        setIsProcessingScan(true);
        setIdCode(scannedValue);

        // Process based on camera mode
        if (cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_HANDOVER ||
            cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_IDENTIFY ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_IDENTIFY ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_IDENTIFY) {
            handleHandover();
            setIsProcessingScan(false);
        } else if (cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_COLLECT_CONFIRM ||
            cameraMode === CAMERA_MODES.CUSTOMER_MAIL_CONFIRM ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_CONFIRM ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.SHOPPER_PICKUP_COLLECT ||
            cameraMode === CAMERA_MODES.SHOPPER_PICKUP_HANDOVER ||
            cameraMode === CAMERA_MODES.PHARMACY_PICKUP_BAG ||
            cameraMode === CAMERA_MODES.PHARMACY_COLLECT_BAG ||
            cameraMode === CAMERA_MODES.PHARMACY_MAIL_BAG) {
            setScannedBagCodes(prev => [...prev, scannedValue]);
            setOrderPackedDrawerOpened(true);
        } else {
            console.error("camera page: camera mode not found");
            setIsProcessingScan(false);
        }
    }, [isProcessingScan, orderPackedDrawerOpened, failDrawerOpened, scannedBagCodes, cameraMode, handleHandover]);

    // Use jsQR scanner on video frames
    useQrScanner(videoRef, handleQrDetected, scannerEnabled);

    console.log('[Camera] Rendering CameraPage', { mode, cameraMode, shouldShowCamera, scannerEnabled });

    return (
        <div className="fixed inset-0">
            {shouldShowCamera && (
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                />
            )}

            <CameraInstructions cameraMode={cameraMode} packages={orderDetails.packages ?? []} />

            {cameraMode === CAMERA_MODES.CUSTOMER_PICKUP_CONFIRM ? (
                <div className='flex justify-center items-center w-full h-full'>
                    <BringzzQrCode value={"123456798"} />
                </div>
            ) : (
                <CameraOverlay />
            )}

            <div className="absolute top-0 left-0 p-5">
                <BringzzIcon
                    size="16"
                    icon="IconClose"
                    folder="LineIcons"
                    className="cursor-pointer bg-white rounded-full p-3"
                    onClick={backPress}
                />
            </div>

            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-6">
                <BringzzButton
                    onClick={() => setFailDrawerOpened(true)}
                    size='md'
                    className='text-white border border-white'
                >
                    QR-Code not working?
                </BringzzButton>
            </div>

            <IdCodeDrawer
                isOpen={failDrawerOpened}
                onClose={() => {
                    setFailDrawerOpened(false);
                    setIsProcessingScan(false);
                }}
                idCode={idCode}
                setIdCode={setIdCode}
                onSubmit={submitCode}
                loading={loading}
            />

            <OrderPackedDrawer
                isOpen={orderPackedDrawerOpened}
                onClose={() => {
                    setOrderPackedDrawerOpened(false);
                    setIsProcessingScan(false);
                }}
                bagCount={bagCount}
                onContinue={onContinue}
                handleConfirm={handleConfirm}
                onAddAnotherBag={onAddAnotherBag}
                loading={loading}
                bagCodes={scannedBagCodes}
                cameraMode={cameraMode}
                packages={orderDetails.packages ?? []}
            />
        </div>
    );
};

export default CameraPage;
